import { z } from 'zod';
import { GoalPeriodKind } from '@apm/contracts';

/**
 * Tab và kỳ của trang OKR, đi qua **URL** — APM-OKR2-e, kế hoạch §14.5.
 *
 * Đây là màn hình người ta gửi link cho nhau mỗi kỳ ("xem OKR quý sau của em
 * nhé"). Giữ trong state React thì mọi link mở ra tab Chung của kỳ hiện tại.
 *
 * ⚠️ `offset` khai bằng `z.number()`, **không** `z.string()`.
 *
 * TanStack Router phân tích search param bằng `JSON.parse`, nên `?offset=1`
 * tới schema dưới dạng **number** `1`. Một `z.string()` sẽ trượt, `.catch()`
 * nuốt lỗi, và tham số biến mất im lặng khỏi URL — mất luôn khả năng chia sẻ
 * đúng cảnh (cạm bẫy APM-903b).
 */
export const OkrTabSchema = z.enum(['overview', ...GoalPeriodKind.options]);
export type OkrTabValue = z.infer<typeof OkrTabSchema>;

export const OkrSearch = z.object({
  /** Bỏ trống = tab Chung. Chỉ trạng thái **khác mặc định** mới lên URL. */
  tab: OkrTabSchema.optional(),
  /**
   * Kỳ đang xem so với kỳ hiện tại. `1` là kỳ kế cận; không đi xa hơn (§14.2).
   *
   * Kẹp ở đây chứ không chỉ ở nút bấm: một người sửa tay `?offset=99` sẽ thấy
   * một kỳ năm 2050 với nút "Thêm" bị từ chối, và không hiểu vì sao.
   */
  offset: z.number().int().min(-24).max(1).optional(),
  /**
   * Tab lớn: `team` = đang xem OKR của nhân sự mình quản lý — người dùng yêu
   * cầu 2026-09-22. Bỏ trống = OKR của chính mình.
   */
  scope: z.enum(['me', 'team']).optional(),
  /**
   * Người đang được chọn ở tab nhóm.
   *
   * ⚠️ `z.string()` ở đây là **đúng**, khác `offset`: uuid là thứ `JSON.parse`
   * không đọc nổi, nên nó tới schema nguyên vẹn dưới dạng chuỗi.
   */
  member: z.string().optional(),
});
export type OkrSearch = z.infer<typeof OkrSearch>;
